import React, { createContext, useState, useEffect } from "react";

const DefaultLanguage = "he";

// Content translations
const translations = {};

// Dynamically require all JSON files in the locales directory
const requireContext = require.context("../locales", true, /\.json$/);

requireContext.keys().forEach((file) => {
  // Extract language and file name
  const [, lang, fileName] = file.match(/\.\/(\w+)\/(.+)\.json$/);

  // Initialize language if not present
  translations[lang] = translations[lang] || {};

  // Assign the JSON content to the correct language and file name
  translations[lang][fileName] = requireContext(file);
});

const LanguageContext = createContext({
  language: "en",
  translations: translations.en,
  toggleLanguage: () => null,
  isRTL: false,
});

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const savedLang = localStorage.getItem("language");
    return savedLang || DefaultLanguage;
  });

  const isRTL = language === "he";

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = language;
    localStorage.setItem("language", language);
  }, [language, isRTL]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "he" : "en"));
  };

  const value = {
    language,
    translations: translations[language],
    toggleLanguage,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export { LanguageProvider, useLanguage, translations };
